import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from './components/AdminSidebar';
import '../../styles/admin.css';

function AdminMediaSettings() {
  const [settings, setSettings] = useState({
    siteName: '',
    logo: '',
    tagline: '',
    homeHero: {
      title: '',
      subtitle: '',
      ctaText: '',
      backgroundType: 'video',
      backgroundMedia: '',
      overlayColor: '#ffffff',
      overlayOpacity: 0.1
    },
    aboutHero: {
      title: '',
      subtitle: '',
      backgroundType: 'animation',
      backgroundMedia: '',
      overlayColor: '#ffffff',
      overlayOpacity: 0
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewUrls, setPreviewUrls] = useState({
    logo: '',
    homeBackground: '',
    aboutBackground: ''
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }
      
      const response = await fetch('/api/admin/settings', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          navigate('/admin/login');
          return;
        }
        throw new Error('Failed to fetch settings');
      }
      
      const data = await response.json();
      setSettings(data);
      
      // Set preview URLs
      setPreviewUrls({
        logo: data.logo || '',
        homeBackground: data.homeHero?.backgroundMedia || '',
        aboutBackground: data.aboutHero?.backgroundMedia || ''
      });
      
      setError('');
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setSettings(prev => {
      if (section) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        };
      } else {
        return {
          ...prev,
          [field]: value
        };
      }
    });
  };

  const handleFileUpload = async (file, type) => {
    setUploading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append(type === 'logo' ? 'image' : 'media', file);
      
      const token = localStorage.getItem('adminToken');
      const endpoint = type === 'logo' ? '/api/admin/upload-image' : '/api/admin/upload-media';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload file');
      }
      
      const data = await response.json();
      const fileUrl = data.imageUrl || data.mediaUrl;
      
      // Update settings and preview
      if (type === 'logo') {
        setSettings(prev => ({ ...prev, logo: fileUrl }));
        setPreviewUrls(prev => ({ ...prev, logo: fileUrl }));
      } else if (type === 'homeBackground') {
        setSettings(prev => ({
          ...prev,
          homeHero: { ...prev.homeHero, backgroundMedia: fileUrl }
        }));
        setPreviewUrls(prev => ({ ...prev, homeBackground: fileUrl }));
      } else if (type === 'aboutBackground') {
        setSettings(prev => ({
          ...prev,
          aboutHero: { ...prev.aboutHero, backgroundMedia: fileUrl }
        }));
        setPreviewUrls(prev => ({ ...prev, aboutBackground: fileUrl }));
      }
      
      setSuccess('File uploaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Error uploading file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save settings');
      }
      
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setError('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar activeItem="media" />
        <div className="admin-content">
          <div className="loading">Loading settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar activeItem="media" />
      
      <div className="admin-content">
        <div className="section-header">
          <h1>Media & Content Settings</h1>
          <button 
            onClick={handleSaveSettings}
            className="save-button"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <div className="settings-grid">
          {/* Logo & Branding Section */}
          <div className="settings-section">
            <h2>Logo & Branding</h2>
            
            <div className="form-group">
              <label htmlFor="siteName">Site Name</label>
              <input
                type="text"
                id="siteName"
                value={settings.siteName}
                onChange={(e) => handleInputChange(null, 'siteName', e.target.value)}
                placeholder="Kentech Phones"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="tagline">Tagline</label>
              <input
                type="text"
                id="tagline"
                value={settings.tagline}
                onChange={(e) => handleInputChange(null, 'tagline', e.target.value)}
                placeholder="Your #1 Phone Repair Specialist"
              />
            </div>
            
            <div className="form-group">
              <label>Logo</label>
              {previewUrls.logo && (
                <div className="media-preview">
                  <img src={previewUrls.logo} alt="Logo preview" style={{maxWidth: '200px', maxHeight: '100px'}} />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'logo')}
                disabled={uploading}
              />
            </div>
          </div>
          
          {/* Home Page Hero Section */}
          <div className="settings-section">
            <h2>Home Page Hero</h2>
            
            <div className="form-group">
              <label htmlFor="homeTitle">Hero Title</label>
              <input
                type="text"
                id="homeTitle"
                value={settings.homeHero.title}
                onChange={(e) => handleInputChange('homeHero', 'title', e.target.value)}
                placeholder="Kentech Phones"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="homeSubtitle">Hero Subtitle</label>
              <input
                type="text"
                id="homeSubtitle"
                value={settings.homeHero.subtitle}
                onChange={(e) => handleInputChange('homeHero', 'subtitle', e.target.value)}
                placeholder="Optional subtitle"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="homeCta">CTA Button Text</label>
              <input
                type="text"
                id="homeCta"
                value={settings.homeHero.ctaText}
                onChange={(e) => handleInputChange('homeHero', 'ctaText', e.target.value)}
                placeholder="Book a Repair"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="homeBackgroundType">Background Type</label>
              <select
                id="homeBackgroundType"
                value={settings.homeHero.backgroundType}
                onChange={(e) => handleInputChange('homeHero', 'backgroundType', e.target.value)}
              >
                <option value="video">Video</option>
                <option value="image">Image</option>
                <option value="animation">Animation</option>
              </select>
            </div>
            
            {settings.homeHero.backgroundType !== 'animation' && (
              <div className="form-group">
                <label>Background {settings.homeHero.backgroundType === 'video' ? 'Video' : 'Image'}</label>
                {previewUrls.homeBackground && (
                  <div className="media-preview">
                    {settings.homeHero.backgroundType === 'video' ? (
                      <video 
                        src={previewUrls.homeBackground} 
                        style={{maxWidth: '300px', maxHeight: '200px'}}
                        controls
                      />
                    ) : (
                      <img 
                        src={previewUrls.homeBackground} 
                        alt="Background preview" 
                        style={{maxWidth: '300px', maxHeight: '200px'}}
                      />
                    )}
                  </div>
                )}
                <input
                  type="file"
                  accept={settings.homeHero.backgroundType === 'video' ? 'video/*' : 'image/*'}
                  onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'homeBackground')}
                  disabled={uploading}
                />
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="homeOverlayColor">Overlay Color</label>
              <input
                type="color"
                id="homeOverlayColor"
                value={settings.homeHero.overlayColor}
                onChange={(e) => handleInputChange('homeHero', 'overlayColor', e.target.value)}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="homeOverlayOpacity">Overlay Opacity ({settings.homeHero.overlayOpacity})</label>
              <input
                type="range"
                id="homeOverlayOpacity"
                min="0"
                max="1"
                step="0.1"
                value={settings.homeHero.overlayOpacity}
                onChange={(e) => handleInputChange('homeHero', 'overlayOpacity', parseFloat(e.target.value))}
              />
            </div>
          </div>
          
          {/* About Page Hero Section */}
          <div className="settings-section">
            <h2>About Page Hero</h2>
            
            <div className="form-group">
              <label htmlFor="aboutTitle">Hero Title</label>
              <input
                type="text"
                id="aboutTitle"
                value={settings.aboutHero.title}
                onChange={(e) => handleInputChange('aboutHero', 'title', e.target.value)}
                placeholder="About Kentech Phones"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="aboutSubtitle">Hero Subtitle</label>
              <textarea
                id="aboutSubtitle"
                value={settings.aboutHero.subtitle}
                onChange={(e) => handleInputChange('aboutHero', 'subtitle', e.target.value)}
                placeholder="Your trusted tech partners for phones, laptops, consoles, VR and PCs."
                rows="3"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="aboutBackgroundType">Background Type</label>
              <select
                id="aboutBackgroundType"
                value={settings.aboutHero.backgroundType}
                onChange={(e) => handleInputChange('aboutHero', 'backgroundType', e.target.value)}
              >
                <option value="animation">Animation</option>
                <option value="video">Video</option>
                <option value="image">Image</option>
              </select>
            </div>
            
            {settings.aboutHero.backgroundType !== 'animation' && (
              <div className="form-group">
                <label>Background {settings.aboutHero.backgroundType === 'video' ? 'Video' : 'Image'}</label>
                {previewUrls.aboutBackground && (
                  <div className="media-preview">
                    {settings.aboutHero.backgroundType === 'video' ? (
                      <video 
                        src={previewUrls.aboutBackground} 
                        style={{maxWidth: '300px', maxHeight: '200px'}}
                        controls
                      />
                    ) : (
                      <img 
                        src={previewUrls.aboutBackground} 
                        alt="Background preview" 
                        style={{maxWidth: '300px', maxHeight: '200px'}}
                      />
                    )}
                  </div>
                )}
                <input
                  type="file"
                  accept={settings.aboutHero.backgroundType === 'video' ? 'video/*' : 'image/*'}
                  onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'aboutBackground')}
                  disabled={uploading}
                />
              </div>
            )}
          </div>
        </div>
        
        {uploading && (
          <div className="upload-overlay">
            <div className="upload-progress">
              <p>Uploading file...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminMediaSettings;